/**
 * poi_manager.js – POI (Point of Interest) management.
 *
 * Exports:
 *  - renderPOIFlags   – render Cesium billboard + number-label entities for a
 *                       list of POIs (shared by scene_loader & loadPOIsForScene)
 *
 * Side-effects on window:
 *  - window.loadPOIsForScene(sceneId) – registered here for backward compat
 */

import { getBaseUrl } from '../../envUtils';

// ---------------------------------------------------------------------------
// Shared POI renderer
// ---------------------------------------------------------------------------

/**
 * Drop Cesium billboard pins + numeric labels for an array of POIs.
 * Clears `window.poiFlagPoints` first, then repopulates it.
 *
 * @param {Array<{name:string, longitude:number, latitude:number, pinIcon:string}>} pois
 */
export const renderPOIFlags = (pois) => {
    const viewer = window.viewer;
    if (!viewer) { console.warn('renderPOIFlags: viewer not ready'); return; }

    window.poiFlagPoints = window.poiFlagPoints || [];
    const height = window.destinationSeaLevelHeight ?? 0;

    const validPois = (pois || []).filter(p => p.name && !p.name.endsWith('undefined )'));
    if (!validPois.length) {
        console.log('ℹ️  No POIs to display');
        window.poiFlagPoints = [];
        return;
    }

    console.log(`📍 Rendering ${validPois.length} POI flags`);

    validPois.forEach((poi, index) => {
        const flag  = new Cesium.PinBuilder();
        const props = new Cesium.PropertyBag();
        props.addProperty('MyProperty_A', 0);
        props.addProperty('MyProperty_B', 1);

        const position = Cesium.Cartesian3.fromDegrees(poi.longitude, poi.latitude, height);

        window.poiFlagPoints.push(viewer.entities.add({
            name: 'pole', position,
            billboard: {
                image: flag.fromUrl(poi.pinIcon, new Cesium.Color.fromCssColorString('#1e90ff99'), 50),
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM
            },
            properties: props
        }));

        window.poiFlagPoints.push(viewer.entities.add({
            name: 'pole', position,
            billboard: {
                image: flag.fromText(index + 1, Cesium.Color.DODGERBLUE, 25).toDataURL(),
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM
            },
            properties: props
        }));
    });

    console.log(`✅ Total POI entities created: ${window.poiFlagPoints.length}`);
};

// ---------------------------------------------------------------------------
// On-demand POI loader (window global)
// ---------------------------------------------------------------------------

/**
 * Fetch and render POIs for a scene on-demand.
 * Safe to call multiple times – skips fetch if already loaded.
 *
 * @param {string} sceneId
 * @returns {Promise<Array>} Array of POI objects
 */
window.loadPOIsForScene = async function loadPOIsForScene(sceneId) {
    if (window._poisLoadedForScene) {
        console.log('ℹ️  POIs already loaded for this scene');
        return window.allPickedAssets;
    }

    console.log('📍 Loading POIs on-demand for scene:', sceneId);

    try {
        const response = await fetch(
            `${getBaseUrl()}/twinworld/getSceneById/${sceneId}?includePOIs=true`,
            { credentials: 'include' }
        );
        if (!response.ok) throw new Error(`Failed to fetch POIs: ${response.status}`);

        const scene = await response.json();
        const pois  = scene.pois
            ? (typeof scene.pois === 'string' ? JSON.parse(scene.pois) : scene.pois)
            : [];

        console.log(`✅ Loaded ${pois.length} POIs`);

        window.allPickedAssets    = pois;
        window._poisLoadedForScene = true;

        // Build entity list for the context panel
        window.entityList = Array.from(new Set(
            pois
                .filter(p => p.name && !p.name.endsWith('undefined )') && p.name !== window.currentAssetName)
                .map(p => (
                    <span>
                        {p.name.split('( Status')[0]}<br />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        {'    ( Status' + p.name.split('( Status')[1]}<br />
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        Lat: {parseFloat(p.latitude).toFixed(3)}&nbsp; Lon: {parseFloat(p.longitude).toFixed(3)}
                    </span>
                ))
        ));

        renderPOIFlags(pois);

        // Trigger context panel refresh
        setTimeout(() => {
            document.querySelector('twinworld-app')?.shadowRoot?.getElementById('contextData')?.click();
        }, 100);

        return pois;

    } catch (error) {
        console.error('❌ Failed to load POIs:', error);
        window.allPickedAssets    = [];
        window.entityList         = [];
        window._poisLoadedForScene = false;
        return [];
    }
};
